import { GetWebsiteNotificationsResponseDTO } from "@/app/api/notifications/notifications.dto";
import { BASE_URL } from "@/lib/constants";
import { TUserNotificationType } from "@/types/app";
import { Menu, Transition } from "@headlessui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { BellIcon } from "lucide-react";
import { Fragment } from "react";
import NotificationCard from "./notifications-card";

const fetchUserNotifications = async () => {
	try {
		const response = await fetch(`${BASE_URL}/api/notifications`, { next: { revalidate: 600 } });
		if (!response.ok) {
			return {
				notifications: [],
				unreadNotificationsCount: 0,
			};
		}

		const { data } = (await response.json()) as GetWebsiteNotificationsResponseDTO;

		return data;
	} catch (error) {
		console.log("Error occured while fetching user notifications");

		return {
			notifications: [],
			unreadNotificationsCount: 0,
		};
	}
};

export default function Notifications() {
	const {
		isLoading: isNotificationsLoading,
		isError: isNotificationsError,
		data,
	} = useQuery({ queryKey: ["websiteNotifications"], queryFn: fetchUserNotifications });

	const queryClient = useQueryClient();
	const notifications = data?.notifications || [];
	const numberOfUnreadNotifications = data?.unreadNotificationsCount || 0;

	const markRecentNotificationsAsRead = async () => {
		try {
			const response = await fetch(`${BASE_URL}/api/notifications`, { method: "PUT" });
			if (!response.ok) {
				return false;
			}

			return true;
		} catch (error) {
			console.log("Error occured while marking recent notifications as read");
			return false;
		}
	};

	const notificationsMutation = useMutation({
		mutationFn: () => markRecentNotificationsAsRead(),
		onSuccess: () => {
			return queryClient.invalidateQueries({ queryKey: ["websiteNotifications"] });
		},
	});

	return (
		<Menu as="div" className="relative inline-block text-left">
			<div>
				<Menu.Button
					onClick={() => notificationsMutation.mutate()}
					className="relative inline-flex w-full justify-center gap-x-1.5 rounded-md border bg-transparent bg-white px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50 hover:ring-1 hover:ring-gray-300 active:ring-inset"
				>
					<span>
						<BellIcon className="h-5 w-5" aria-hidden="true" />
					</span>

					{numberOfUnreadNotifications > 0 ? (
						<span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-xxs font-medium text-white">
							{numberOfUnreadNotifications}+
						</span>
					) : null}
				</Menu.Button>
			</div>

			<Transition
				as={Fragment}
				enter="transition ease-out duration-100"
				enterFrom="transform opacity-0 scale-95"
				enterTo="transform opacity-100 scale-100"
				leave="transition ease-in duration-75"
				leaveFrom="transform opacity-100 scale-100"
				leaveTo="transform opacity-0 scale-95"
			>
				<Menu.Items className="absolute right-0 z-20 mt-2 w-80 origin-top-right rounded-md bg-white shadow ring-1 ring-black ring-opacity-5 focus:outline-none">
					{isNotificationsError ? (
						<span>Error occured while fetching recent notifications</span>
					) : isNotificationsLoading ? (
						<span>Fetching...</span>
					) : (
						<div className="p-4">
							<div className="font-sm mb-2 font-semibold text-gray-600">Notifications</div>

							<div className="customized-scrollbar mb-4 max-h-96 space-y-2 overflow-y-scroll pb-2">
								{notifications && notifications.length > 0 ? (
									notifications.map((notification) => (
										<Menu.Item key={notification.id}>
											<NotificationCard
												title={notification.text}
												description=""
												image={notification.sender.image}
												isVerified={notification.sender.isVerified}
												isFounder={notification.sender.isFounder}
												notificationType={notification.notificationType as TUserNotificationType}
												link={notification.link}
												time={formatDistanceToNow(new Date(notification.createdAt), {
													addSuffix: true,
												})}
											/>
										</Menu.Item>
									))
								) : (
									<span className="block text-center text-sm text-gray-500">
										No recent notifications
									</span>
								)}
							</div>
						</div>
					)}
				</Menu.Items>
			</Transition>
		</Menu>
	);
}
